import React, { useState, ChangeEvent, useEffect, useMemo } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Box,
  Divider,
  Stack,
  CircularProgress,
  TextField,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { theme } from '@/theme/theme';
import {
  FileNameTypes,
  GetAssemblyAttachmentsDocument,
  GetAssemblyAttachmentsQuery,
  GetAssemblyDetailsDocument,
  ImageType,
  PaintBlockPointsDocument,
  useDeleteFilesMutation,
  useUpdateFileStatusMutation,
  useUploadFilesMutation,
} from '@/__generated__/graphql';
import { FileUploadStatus } from '@/__generated__/graphql';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { useGetPdfImages } from '@/hooks/useGetPdfImages';

import validateFiles from '../../modules/pointOfInterest/PointOfInterestTab/SelectedPoiContent/Tabs/ValidateFiles';
import { FileDisplay } from './FileDisplay';
import { MAX_FILE_UPLOAD_LIMIT } from '@/constants';
import { Attachment } from '@/components/shared/AttachmentDisplay/type';
import { FileTypeDialog } from './FileTypeDialog';
import { FileType } from '@/types';
import { abyssColors } from '@/theme/colors';
import { useAssemblyNoteMutation } from '@/hooks/useAssemblyNoteMutation';

type Props = {
  openDialog: boolean;
  setOpenDialog: React.Dispatch<React.SetStateAction<boolean>>;
  referenceId?: string;
  collectionName: string;
  attachments: Attachment[];
  dialogTitle: string;
  buttonText: string;
  enableUpload?: boolean;
  setAttachments: React.Dispatch<React.SetStateAction<Attachment[]>>;
  setIsUpdating: React.Dispatch<React.SetStateAction<boolean>>;
  location: string;
  assembly?: GetAssemblyAttachmentsQuery['assembly'];
};

const convertToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.addEventListener('load', () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Result of file reading was not a string.'));
      }
    });
    reader.addEventListener('error', () => reject(reader.error));
  });

const UploadFileDialog = ({
  openDialog,
  setOpenDialog,
  referenceId,
  collectionName,
  attachments,
  setAttachments,
  setIsUpdating,
  buttonText,
  dialogTitle,
  enableUpload,
  location,
  assembly,
}: Props) => {
  const structureId = useGetStructureId();
  const [UpdateFileStatus] = useUpdateFileStatusMutation();
  const [uploadedAttachments, setUploadedAttachments] = useState<Attachment[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const [removeAttachments, setRemoveAttachments] = useState<string[]>([]);
  const [fileDialog, setFileDialog] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState<File[] | null>(null);
  const [note, setNote] = useState<string | undefined | null>(assembly?.generalAttachmentsNote);
  const [uploadFiles] = useUploadFilesMutation();
  const [deleteFiles] = useDeleteFilesMutation();
  const { updateNote } = useAssemblyNoteMutation();
  const { showSnackBar } = useSnackBarMessage({
    variant: 'filled',
  });
  const { getImages } = useGetPdfImages();

  useEffect(() => {
    setNote(assembly?.generalAttachmentsNote);
  }, [assembly?.generalAttachmentsNote]);

  useEffect(() => {
    setUploadedAttachments(attachments);
  }, [attachments]);

  useEffect(() => {
    setNote(assembly?.generalAttachmentsNote);
  }, [assembly?.generalAttachmentsNote]);

  const handleAddAttachment = (event: ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;
    if (!files || files.length === 0) return;
    const filesArray = Array.from(files);
    const hasImage = filesArray.some((file) => file.type.startsWith('image/'));
    if (hasImage) {
      processAttachments(filesArray, FileType.GENERAL);
    } else {
      setPendingAttachments(filesArray);
      setFileDialog(true);
    }
  };

  const processAttachments = async (files: File[], type: string) => {
    if (!files) return;
    if (uploadedAttachments.length + files.length > MAX_FILE_UPLOAD_LIMIT) {
      showSnackBar(
        `You can only upload a maximum of ${MAX_FILE_UPLOAD_LIMIT} files in total.`,
        'error'
      );
      return;
    }
    setIsUploading(true);
    const { validFiles, fileErrors } = validateFiles(files);

    if (fileErrors.length > 0) {
      showSnackBar(
        `You can only upload a maximum of ${MAX_FILE_UPLOAD_LIMIT} files in total.`,
        'error'
      );
      fileErrors.map((error) => showSnackBar(error, 'error'));
      return;
    }

    const duplicateFiles = validFiles.filter((file) =>
      uploadedAttachments.some((att) => att.name === file.name.replace(/[^\w.-]/g, '_'))
    );

    if (duplicateFiles.length > 0) {
      showSnackBar(
        `Duplicate file names found: ${duplicateFiles.map((file) => file.name).join(', ')}`,
        'error'
      );
      return;
    }

    const attachmentsPayload: Attachment[] = [];
    for (let index = 0; index < validFiles.length; index++) {
      const file = validFiles[index];

      const isImageOrVideo = file.type.startsWith('image/');
      const base64 = isImageOrVideo ? await convertToBase64(file) : '';
      if (file.type === 'application/pdf' && type === FileType.REFERENCE_DRAWING) {
        const images = await getImages(file);

        const referenceDrawing: Attachment[] = images.map((image, imageIndex) => ({
          uuid: `file-${Date.now()}-${imageIndex}`,
          name: image.name,
          type: 'image/png',
          base64: image.base64,
          isNew: true,
          size: image.size,
          file: image.file,
          uploadStatus: FileUploadStatus.Inprogress,
          fileNameType: FileNameTypes.ReferenceDrawing,
        }));

        attachmentsPayload.push(...referenceDrawing);
        setIsUploading(false);
      } else {
        setIsUploading(false);
        attachmentsPayload.push({
          uuid: `file-${Date.now()}-${index}`,
          name: file.name,
          type: file.type,
          base64,
          isNew: true,
          size: file.size,
          file,
          uploadStatus: FileUploadStatus.Inprogress,
          fileNameType: FileNameTypes.UserAttachments,
        });
      }
    }
    setUploadedAttachments((previousAttachments) => [
      ...previousAttachments,
      ...attachmentsPayload,
    ]);
  };

  const handleRemoveAttachment = (index: number) => {
    const displayedAttachment = attachmentsShown[index];

    if (!displayedAttachment) return;

    const actualIndex = uploadedAttachments.findIndex(
      (att) =>
        att.id === displayedAttachment.id ||
        (att.uuid === displayedAttachment.uuid && att.name === displayedAttachment.name)
    );

    if (actualIndex === -1) return;

    const attachmentToRemove = uploadedAttachments[actualIndex];
    const newAttachments = [...uploadedAttachments];
    newAttachments.splice(actualIndex, 1);

    if (attachmentToRemove && !attachmentToRemove.isNew && attachmentToRemove.id) {
      const idsToRemove = [attachmentToRemove.id];

      const whiteBgPdfAttachment = uploadedAttachments.find(
        (attachment) =>
          attachment.id !== attachmentToRemove.id &&
          attachment.uuid === `${attachmentToRemove.uuid}-${ImageType.WhiteBgPdf}` &&
          attachment.imageType === ImageType.WhiteBgPdf
      );

      if (whiteBgPdfAttachment?.id) {
        idsToRemove.push(whiteBgPdfAttachment.id);
        const whiteBgPdfIndex = newAttachments.findIndex(
          (att) => att.id === whiteBgPdfAttachment.id
        );
        if (whiteBgPdfIndex !== -1) {
          newAttachments.splice(whiteBgPdfIndex, 1);
        }
      }

      setRemoveAttachments((previousRemovedAttachments) => [
        ...previousRemovedAttachments,
        ...idsToRemove,
      ]);
    }

    setUploadedAttachments(newAttachments);
  };

  const attachmentsShown = useMemo(() => {
    return uploadedAttachments.filter(
      (attachment) => attachment.imageType !== ImageType.WhiteBgPdf
    );
  }, [uploadedAttachments]);

  const handleClose = () => {
    setOpenDialog(false);
    setRemoveAttachments([]);
    setUploadedAttachments(attachments);
  };

  const handleUpdate = async () => {
    const attachmentsToBeUploaded = uploadedAttachments.filter((file) => file.isNew);

    if (!referenceId) {
      showSnackBar('RefrenceId is required', 'error');
      return;
    }

    if (note !== assembly?.generalAttachmentsNote) {
      try {
        setIsUploading(true);
        await updateNote(note, referenceId, structureId);
        setIsUploading(false);
      } catch (error) {
        showSnackBar('Failed to add note', 'error');
      }
      if (attachmentsToBeUploaded.length === 0 && removeAttachments.length === 0) {
        showSnackBar('Note added successfully', 'success');
      }
    }

    if (removeAttachments.length > 0) {
      setIsUpdating(true);
      setIsUploading(false);
      setOpenDialog(false);
      setAttachments(() =>
        attachments.filter(
          (attachment) => attachment.id && !removeAttachments.includes(attachment.id)
        )
      );
      await deleteFiles({
        variables: {
          input: {
            referenceId,
            collectionName,
            fileIds: removeAttachments,
          },
        },
        refetchQueries: [GetAssemblyDetailsDocument, PaintBlockPointsDocument],
        onCompleted: () => {
          setIsUpdating(false);
          showSnackBar('Attachments have been successfully updated', 'success');
        },
        onError: (error) => {
          setIsUpdating(false);
          showSnackBar(error.message, 'error');
        },
      });
    }

    const handleUploadingFiles = async (fileNameType: FileNameTypes) => {
      const attachments = attachmentsToBeUploaded.filter(
        (file) => file.fileNameType === fileNameType
      );

      if (!attachments.length) return null;
      if (!referenceId) {
        showSnackBar('RefrenceId is required', 'error');
        return;
      }
      setIsUploading(true);
      setIsUpdating(true);
      return uploadFiles({
        variables: {
          input: {
            structureId,
            collectionName,
            referenceId,
            files: attachments.map(({ uuid, name, type, size }) => ({
              uuid: uuid || '',
              name,
              size: size || 0,
              mimeType: type,
            })),
            fileNameType,
          },
        },
        onError: (error) => {
          setIsUpdating(false);
          showSnackBar(error.message, 'error');
        },
      });
    };

    const [userResult, isoResult] = await Promise.all([
      handleUploadingFiles(FileNameTypes.UserAttachments),
      handleUploadingFiles(FileNameTypes.ReferenceDrawing),
    ]);

    const allUploadedFiles = [
      ...(userResult?.data?.preSignedUrlToUploadFiles || []),
      ...(isoResult?.data?.preSignedUrlToUploadFiles || []),
    ];

    if (allUploadedFiles.length > 0) {
      const updatedAttachments = uploadedAttachments.map((attachment) => {
        const uploadedFile = allUploadedFiles.find((uploaded) => uploaded.uuid === attachment.uuid);
        if (uploadedFile) {
          return {
            ...attachment,
            url: uploadedFile.url,
            resourcePath: uploadedFile.resourcePath,
            id: uploadedFile.id,
          };
        }
        return attachment;
      });
      setAttachments(updatedAttachments);
    }

    setRemoveAttachments([]);
    setUploadedAttachments([]);
    setIsUploading(false);
    setOpenDialog(false);
  };

  const handelUpdateAttachmentStatus = async (fileId: string | undefined) => {
    if (!referenceId) {
      showSnackBar('RefrenceId is required', 'error');
      return;
    }
    if (!fileId) return;
    await UpdateFileStatus({
      variables: {
        input: {
          referenceId,
          collectionName,
          fileUpdates: [
            {
              uploadStatus: FileUploadStatus.Success,
              fileId,
            },
          ],
        },
      },
      refetchQueries: [GetAssemblyAttachmentsDocument],
      onCompleted: () => {
        showSnackBar('Attachments have been successfully added', 'success');
      },
      onError: (error) => {
        showSnackBar(error.message, 'error');
      },
    });
  };

  const handleConfirm = (type: string) => {
    if (pendingAttachments) {
      processAttachments(pendingAttachments, type);
    }
    setFileDialog(false);
  };

  const handleNoteChange = (event: ChangeEvent<HTMLInputElement>) => {
    setNote(event.target.value);
  };

  return (
    <>
      <Dialog
        open={openDialog}
        onClose={isUploading ? undefined : handleClose}
        disableEscapeKeyDown={isUploading}
        aria-labelledby="form-dialog-title"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle id="form-dialog-title">{dialogTitle}</DialogTitle>
        <Divider />
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ color: theme.palette.primary.dark }}>
              Write your note here
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, marginLeft: 1 }}>
              (max 500 characters allowed)
            </Typography>
          </Box>
          <TextField
            margin="dense"
            id="note"
            type="text"
            fullWidth
            multiline
            rows={4}
            value={note}
            onChange={handleNoteChange}
            inputProps={{ maxLength: 500 }}
          />
          <Box sx={{ minHeight: '200px' }}>
            <Typography variant="caption" sx={{ color: theme.palette.primary.dark }}>
              Upload an image, video or pdf{'  '}
            </Typography>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
              (max {MAX_FILE_UPLOAD_LIMIT} uploads)
            </Typography>
            <Grid container spacing={2.5} sx={{ my: 3 }}>
              <Grid>
                <label htmlFor="file-upload">
                  <input
                    accept="image/jpeg,image/png,application/pdf,video/mp4"
                    style={{ display: 'none' }}
                    id="file-upload"
                    multiple
                    type="file"
                    onChange={handleAddAttachment}
                  />
                  <Box
                    sx={{
                      border: `0.1rem solid ${abyssColors.primary[200]}`,
                      borderRadius: '0.4rem',
                      alignItems: 'center',
                      justifyContent: 'center',
                      display: 'flex',
                      flexDirection: 'column',
                      width: '10rem',
                      height: '10rem',
                    }}
                  >
                    <ArrowUpwardIcon style={{ color: theme.palette.text.secondary }} />
                    <Typography
                      variant="caption"
                      style={{ color: theme.palette.text.secondary, marginTop: 8 }}
                    >
                      Upload
                    </Typography>
                  </Box>
                </label>
              </Grid>
              {attachmentsShown.map((attachment, index) => (
                <Grid key={attachment.id || attachment.name}>
                  <FileDisplay
                    referenceId={referenceId}
                    collectionName={collectionName}
                    selectedAttachment={attachment}
                    selectedAttachmentIndex={index}
                    handleRemoveAttachment={handleRemoveAttachment}
                    uploadAttachmentHandler={() => handelUpdateAttachmentStatus(attachment.id)}
                    enableUpload={enableUpload}
                    setIsUpdating={setIsUpdating}
                    isUploadDialog={true}
                    showRemoveIcon
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ justifyContent: 'space-between', px: 2 }}>
          <Stack
            direction="row"
            justifyContent="flex-start"
            spacing={2}
            sx={{ mx: '12px' }}
          ></Stack>
          <Stack direction="row" justifyContent="flex-end" spacing={2}>
            <Button
              onClick={handleClose}
              color="primary"
              variant="outlined"
              sx={{ borderRadius: 2 }}
              id="media-upload-dialog-cancel-button"
            >
              Cancel
            </Button>
            <Button
              id="media-upload-dialog-update-button"
              onClick={handleUpdate}
              color="primary"
              variant="contained"
              sx={{ borderRadius: 2, background: theme.palette.primary.dark, marginRight: '12px' }}
              disabled={isUploading}
            >
              {isUploading ? <CircularProgress size={20} /> : buttonText}
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
      <FileTypeDialog
        setFileDialog={setFileDialog}
        open={fileDialog}
        onConfirm={handleConfirm}
        location={location}
      />
    </>
  );
};

export default UploadFileDialog;
